<script lang="ts">
  import type { FacetValue, FacetValueOptions } from "./type.ts";
  import { Text } from "../facet-value-label";
  import { featureTrackingAttributes, removeStatusIf } from "../tracking";
  import { preselected } from "../facet/preselection.svelte.ts";
  import type { ClassValue } from "svelte/elements";

  interface Props extends FacetValue, FacetValueOptions {
    class?: ClassValue;
    excludeFromForm?: boolean;
    selectedValues?: string[];
    variantGray?: boolean;
    fillParent?: boolean;
    showRemoveIcon?: boolean;
  }

  let {
    id, /*                                        */
    children = text,
    selection = "multi",
    saved = false,
    featureTracking,
    "aria-label": ariaLabel,
    checked,
    popular,
    nba,
    itemCount,
    referencedFacetValueId,
    excludeFromForm,
    variantGray,
    fillParent = false,
    showRemoveIcon = false,
    selectedValues = $bindable(),
    ...rest
  }: Props = $props();

  /*                     */
  const tracking = $derived(removeStatusIf(!checked && !popular, featureTracking));
  let dataChecked = $state(!!checked);

  /*                            */
  ariaLabel ||= rest.forceAriaLabel ? rest.title : undefined;

  /*                   */
  let dangerousItemCountClass = itemCount ? "" : "heureka_facetValue__dangerousZeroHits";

  function onchange({ currentTarget }: Event & { currentTarget: HTMLInputElement }) {
    /*                                        */
    dataChecked = currentTarget.checked;
    selectedValues = currentTarget.checked
      ? [...(selectedValues || []), currentTarget.value]
      : selectedValues?.filter((v) => v !== currentTarget.value);
  }

  const shouldBeSelected = $derived.by(() => {
    if (!id) return checked;

    if (Object.keys($preselected).includes(id)) {
      return $preselected[id];
    } else {
      return checked;
    }
  });
</script>

{#snippet text()}
  <Text size="125" {...{ ...rest, showCount: false }} bold={!!checked} />
{/snippet}

<oc-chip-v2
  {id}
  class={[
    "hc_chip hc_facetValueInput js_hc_tValue",
    excludeFromForm && "hc_facetValueInput--noSubmission",
    dangerousItemCountClass,
    rest.class,
  ]}
  class:hc_chip--always-gray={variantGray}
  {selection}
  fill-parent={fillParent ? "" : undefined}
  show-remove-icon={showRemoveIcon ? "" : undefined}
  name={excludeFromForm ? undefined : rest.name}
  value={rest.value}
  as-button=""
  checked={shouldBeSelected ? "" : undefined}
  icon={saved ? "wishlist-active" : undefined}
  data-checked={dataChecked}
  data-default-checked={!!checked}
  aria-label={ariaLabel}
  {...featureTrackingAttributes(tracking)}
  data-ts-feature-ref={referencedFacetValueId}
  {...nba ? { "data-nba": true } : {}}
  {onchange}
>
  <span>{@render children?.()}</span>
</oc-chip-v2>

<style lang="scss">
  .hc_chip {
    --checked-font-weight: bold;
    &--always-gray {
      --checked-font-weight: normal;
    }

    min-width: 59px;

    /*                               */
    @at-root :global(.hc_chip[data-checked="true"] .hc_label) {
      font-weight: var(--checked-font-weight);
    }

    @at-root :global(.hc_chip[data-checked="true"] .hc_color .hc_color__title) {
      font-weight: var(--checked-font-weight);
    }
  }
</style>
